import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import api from '../services/api';
import { NotificationContext } from './NotificationContext';

export interface RenewalAlert {
  id: string; source: 'automation' | 'purchase' | 'external'; referenceId: string; domainName: string; code: string;
  severity: 'critical' | 'warning'; expiresAt: string | null; daysRemaining: number | null;
  actionPath: string; managePath: string;
  tr: { title: string; message: string; action: string }; en: { title: string; message: string; action: string };
}
interface State { alerts: RenewalAlert[]; checkedAt: string | null; emailEnabled: boolean; loading: boolean; error: boolean; refresh: () => Promise<void> }
const Context = createContext<State | null>(null);

export function RenewalAlertsProvider({ children }: { children: ReactNode }) {
  const { connection } = useContext(NotificationContext);
  const [alerts, setAlerts] = useState<RenewalAlert[]>([]);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const request = useRef<AbortController | null>(null);
  const refresh = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    setLoading(true);
    try {
      const { data } = await api.get('/renewal-alerts', { signal: controller.signal, timeout: 15000 });
      if (controller.signal.aborted) return;
      if (!Array.isArray(data?.alerts)) throw new Error('Invalid alert response');
      setAlerts(data.alerts); setCheckedAt(data.checkedAt); setEmailEnabled(data.emailEnabled === true); setError(false);
    } catch { if (!controller.signal.aborted) setError(true); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  }, []);
  useEffect(() => {
    void refresh();
    const onFocus = () => { if (!document.hidden) void refresh(); };
    const timer = setInterval(onFocus, 60000);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); request.current?.abort(); };
  }, [refresh]);
  useEffect(() => {
    if (!connection) return;
    const update = () => { void refresh(); };
    connection.on('ReceiveDomainUpdate', update); connection.on('ReceiveSslOrderUpdate', update);
    return () => { connection.off('ReceiveDomainUpdate', update); connection.off('ReceiveSslOrderUpdate', update); };
  }, [connection, refresh]);
  return <Context.Provider value={{ alerts, checkedAt, emailEnabled, loading, error, refresh }}>{children}</Context.Provider>;
}

export function useRenewalAlerts() {
  const context = useContext(Context);
  if (!context) throw new Error('RenewalAlertsProvider is required');
  return context;
}

// All actions are navigation only. Never permit an external URL in a notification.
export const safeAlertPath = (path: string) => /^\/dashboard(?:[/?#]|$)/.test(path) && !/[\\\r\n]/.test(path) ? path : '/dashboard/alerts';
